import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { HttpException } from 'utils/types';
import { IncomeType } from '../constants';
import { GooglePlaceLocation, SharedWithUser, UnderwritingProperty } from '../types';
import {
  ShareAccessEvent,
  CancelShareEvent,
  UnderwritingAccessStore,
} from 'stores/underwriting/UnderwritingAccessStore';

export class UnderwritingDashboardPageStore {
  message: string = 'Loading...';
  loadStatus: 'pending' | 'loading' | 'error' | 'loaded' = 'pending';

  properties: UnderwritingProperty[] = [];
  shareAccessStore?: UnderwritingAccessStore | null;

  constructor(private readonly authStore: AuthStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async load() {
    try {
      runInAction(() => {
        this.message = 'Loading...';
        this.loadStatus = 'loading';
      });

      const response = await this.authStore.fetchWithAuth(`${serverUri}/underwriting`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const exception: HttpException = nullsToUndefined(await response.json());
        runInAction(() => {
          this.message = exception.message;
          this.loadStatus = 'error';
        });
        return;
      }
      const data: any[] = await response.json();
      this.properties = data.map((property) =>
        nullsToUndefined({
          id: property.id,
          streetAddress: property.address.street,
          propertyType: property.propertyType,
          residentialUnits: property.residentialUnits,
          commercialUnits: property.commercialUnits,
          sharedWith: property.sharedWith,
        })
      );

      runInAction(() => {
        this.loadStatus = 'loaded';
        this.message = 'Loaded successfully';
      });
    } catch (error) {
      runInAction(() => {
        this.message = (error as Error).message;
        this.loadStatus = 'error';
      });
    }
  }

  async create({
    location,
    approaches,
    incomeType,
    valueConclusions,
  }: {
    location: GooglePlaceLocation;
    approaches: string[];
    incomeType: string;
    valueConclusions: string[];
  }) {
    try {
      runInAction(() => {
        this.message = 'Loading...';
        this.loadStatus = 'loading';
      });

      const response = await this.authStore.fetchWithAuth(`${serverUri}/underwriting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location,
          approaches,
          valueConclusions,
          incomeTypes:
            incomeType === IncomeType.residentialAndCommercial
              ? [IncomeType.residential, IncomeType.commercial]
              : [incomeType],
        }),
      });
      if (!response.ok) {
        const exception: HttpException = nullsToUndefined(await response.json());
        runInAction(() => {
          this.message = exception.message;
          this.loadStatus = 'error';
        });
        throw new Error(exception.message);
      }
      const data: any = await response.json();
      const newProperty: UnderwritingProperty = nullsToUndefined({
        id: data.id,
        streetAddress: data.property.address.street,
        propertyType: data.property.propertyType,
        residentialUnits: data.property.residentialUnits,
        commercialUnits: data.property.commercialUnits,
      });

      this.properties.push(newProperty);

      runInAction(() => {
        this.loadStatus = 'loaded';
        this.message = 'Loaded successfully';
      });

      return newProperty;
    } catch (error) {
      runInAction(() => {
        this.message = (error as Error).message;
        this.loadStatus = 'error';
      });
      throw error;
    }
  }

  setSharedWith(id: string, sharedWith: SharedWithUser[]) {
    if (!sharedWith || !Array.isArray(sharedWith)) {
      return;
    }

    const targetProperty = this.properties.find((property) => property.id === id);
    if (targetProperty) {
      targetProperty.sharedWith = sharedWith;
    }
  }

  showShareModal(property: UnderwritingProperty) {
    this.shareAccessStore = new UnderwritingAccessStore(
      this.authStore,
      property.id,
      property.sharedWith || [],
      this.setSharedWith
    );

    this.shareAccessStore.events.addEventListener(ShareAccessEvent.eventType, () => {
      this.shareAccessStore = undefined;
    });
    this.shareAccessStore.events.addEventListener(CancelShareEvent.eventType, () => {
      this.shareAccessStore = undefined;
    });
  }
}
